import { ApiError } from "./errors";

/**
 * Maps a payment failure (anything thrown by payOrder / payOrderMerge /
 * payByBalance) to a STABLE i18n key under the `pay_choose.errors` namespace.
 *
 * P1 information-leak fix (#411): the Java backend surfaces payment failures as
 * a Jeecg `Result.error(code + ":" + message)` envelope — HTTP 200 with
 * `{ code: 500, message: "TWP_RETURN_FAIL:TWP 返回失败 code=AMOUNT_MORE_THAN_CAP, msg=单笔交易金额大于上限" }`.
 * apiFetch puts that whole Chinese/technical string into `ApiError.message`
 * (and `ApiError.code` is only the useless envelope "500"). The cashier page
 * used to render `e.message` verbatim, dumping `TWP_RETURN_FAIL` /
 * `AMOUNT_MORE_THAN_CAP` / Chinese text at Russian end users.
 *
 * This function NEVER returns the raw backend string. It returns one of:
 *   - `errors.code.<known>` for a recognised granular code, or
 *   - `errors.generic` for anything unknown / unparseable.
 * The caller resolves the key through next-intl `t()`, so the only bytes that
 * ever reach the UI are translator-authored.
 *
 * The granular code is recovered best-effort from the message because the
 * backend does not expose a clean `errorCode` field yet (see #411 — a
 * structured field is a follow-up). Two signals are read, both case-insensitively:
 *   1. an embedded upstream-gateway token `code=XXX` (e.g. AMOUNT_MORE_THAN_CAP),
 *   2. the DTO-code prefix before the first `:` (e.g. TWP_RETURN_FAIL).
 * Matching is keyword-based so it survives wording changes from the gateway.
 */

const GENERIC_KEY = "errors.generic";

type CodeKey =
  | "amount_more_than_cap"
  | "amount_less_than_floor"
  | "insufficient_balance"
  | "daily_limit"
  | "risk_rejected"
  | "channel_unavailable"
  | "timeout"
  | "duplicate";

/**
 * Recognised raw-token substrings → friendly key. Ordered roughly by
 * specificity; the first hit wins. All comparisons are lowercased.
 */
const TOKEN_RULES: ReadonlyArray<readonly [substr: string, key: CodeKey]> = [
  ["amount_more_than_cap", "amount_more_than_cap"],
  ["more_than_cap", "amount_more_than_cap"],
  ["exceed", "amount_more_than_cap"],
  ["amount_less_than", "amount_less_than_floor"],
  ["less_than_floor", "amount_less_than_floor"],
  ["less_than_min", "amount_less_than_floor"],
  ["below_min", "amount_less_than_floor"],
  ["insufficient", "insufficient_balance"],
  ["balance_not_enough", "insufficient_balance"],
  ["not_enough", "insufficient_balance"],
  ["daily_limit", "daily_limit"],
  ["payment_daily_limit", "daily_limit"],
  ["risk", "risk_rejected"],
  ["blacklist", "risk_rejected"],
  ["forbidden", "risk_rejected"],
  ["channel_order_id_exists", "duplicate"],
  ["duplicate", "duplicate"],
  ["repeat", "duplicate"],
  ["timeout", "timeout"],
  ["timed_out", "timeout"],
  ["not_configured", "channel_unavailable"],
  ["no_prepay_url", "channel_unavailable"],
  ["sign_invalid", "channel_unavailable"],
  ["http_error", "channel_unavailable"],
  ["channel_unavailable", "channel_unavailable"],
  ["unavailable", "channel_unavailable"],
];

/**
 * Extracts the raw, lowercased token surface from an error to match against the
 * rules. Reads the ApiError message and code; for a plain Error reads message.
 * Returns "" when nothing usable is present.
 */
function rawSurface(error: unknown): string {
  if (error instanceof ApiError) {
    // message carries the "DTOCODE:... code=GATEWAYCODE, msg=..." payload;
    // code is usually just the "500" envelope, but include it defensively.
    return `${error.code} ${error.message}`.toLowerCase();
  }
  if (error instanceof Error) return error.message.toLowerCase();
  if (typeof error === "string") return error.toLowerCase();
  return "";
}

/**
 * Returns a stable i18n key under `pay_choose.errors` for the given payment
 * error. Always returns a key — never the raw backend message. Unknown or
 * unparseable errors fall back to `errors.generic`.
 */
export function paymentErrorKey(error: unknown): string {
  const surface = rawSurface(error);
  if (!surface) return GENERIC_KEY;
  for (const [substr, key] of TOKEN_RULES) {
    if (surface.includes(substr)) return `errors.code.${key}`;
  }
  return GENERIC_KEY;
}
